import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import api from "../../api";

const PURPLE = "#7c3aed";

// Mirrors config('constants.product_types') on the API.
const TYPE_MOCK = 2;
const TYPE_PAPER = 3;

/**
 * Mock exams and past papers included with this course.
 *
 * Anyone who has paid for the course gets these free, computed live — attaching
 * an item unlocks it immediately for students already enrolled, and removing it
 * takes access away again.
 */
export default function CourseIncludedPapersTab({ courseId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [attached, setAttached] = useState([]);
  const [available, setAvailable] = useState({ mock_exams: [], papers: [] });
  const [selection, setSelection] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`admin/courses/${courseId}/resources`);
      const data = res.data?.data || {};
      setAttached(data.attached || []);
      setAvailable(data.available || { mock_exams: [], papers: [] });
    } catch {
      toast.error("Could not load the included papers.");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    load();
  }, [load]);

  // One picker for both kinds — an admin thinks "add this paper", not "pick a type first".
  const options = useMemo(() => {
    const attachedKeys = new Set(attached.map((row) => `${row.resource_type}:${row.resource_id}`));
    const rows = [
      ...(available.mock_exams || []).map((m) => ({
        resource_type: TYPE_MOCK,
        resource_id: m.id,
        name: m.name,
        kind: "Mock exam",
      })),
      ...(available.papers || []).map((p) => ({
        resource_type: TYPE_PAPER,
        resource_id: p.id,
        name: p.name,
        kind: "Past paper",
      })),
    ];
    return rows.filter((row) => !attachedKeys.has(`${row.resource_type}:${row.resource_id}`));
  }, [attached, available]);

  const add = async () => {
    if (!selection) return;
    setSaving(true);
    try {
      await api.post(`admin/courses/${courseId}/resources`, {
        resource_type: selection.resource_type,
        resource_id: selection.resource_id,
      });
      setSelection(null);
      await load();
      toast.success("Added to this course.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not add that item.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    try {
      await api.delete(`admin/courses/${courseId}/resources/${row.id}`);
      setAttached((rows) => rows.filter((r) => r.id !== row.id));
      toast.success("Removed from this course.");
    } catch {
      toast.error("Could not remove that item.");
    }
  };

  if (loading) return <CircularProgress size={28} />;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Students who have paid for this course get these free. Everyone else still pays per item.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
        <Autocomplete
          sx={{ flex: 1, minWidth: 260 }}
          options={options}
          value={selection}
          onChange={(_, value) => setSelection(value)}
          groupBy={(option) => option.kind}
          getOptionLabel={(option) => option.name || ""}
          isOptionEqualToValue={(a, b) =>
            a.resource_type === b.resource_type && a.resource_id === b.resource_id
          }
          renderInput={(params) => <TextField {...params} label="Add a mock exam or past paper" />}
        />
        <Button
          variant="contained"
          onClick={add}
          disabled={!selection || saving}
          sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: "#6d28d9" }, alignSelf: { sm: "center" } }}
        >
          {saving ? "Adding…" : "Add"}
        </Button>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              Remove
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {attached.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3}>
                <Typography variant="body2" color="text.secondary">
                  Nothing included yet.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            attached.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.name}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.kind === "mock_exam" ? "Mock exam" : "Past paper"}
                    sx={{ bgcolor: "#ede9fe", color: PURPLE, fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" aria-label="Remove" onClick={() => remove(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
